import React, { useEffect, useMemo, useState } from 'react';
import {
  X,
  Clock,
  Sparkles,
  RefreshCw,
  Check,
  AlertCircle,
  ArrowRight,
  CheckCircle2,
  Loader2,
} from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '@/lib/supabase';
import { PlanDay, updateFrame } from '@/lib/contentPlanner';

interface Suggestion {
  id: string;
  suggested_time: string;
  reason: string;
}

interface Row {
  frame_id: string;
  day_label: string;
  date_label: string;
  theme: string;
  post_type: string;
  current_time: string;
  approval_status: string;
  caption_snippet: string;
  // Filled after AI returns:
  suggested_time?: string;
  reason?: string;
  applied?: boolean;
  selected: boolean;
}

interface Props {
  open: boolean;
  onClose: () => void;
  days: PlanDay[];
  onUpdated?: () => void;
}

const AutoScheduleModal: React.FC<Props> = ({ open, onClose, days, onUpdated }) => {
  const [phase, setPhase] = useState<'idle' | 'loading' | 'review' | 'applying' | 'done' | 'error'>(
    'idle',
  );
  const [rows, setRows] = useState<Row[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [modelUsed, setModelUsed] = useState<string>('');
  const [includeDraftOnly, setIncludeDraftOnly] = useState(true);

  // Build the candidate row list whenever the modal opens or filter changes
  const candidates = useMemo<Row[]>(() => {
    const out: Row[] = [];
    days.forEach((d) => {
      d.frames.forEach((f) => {
        if (f.post_type === 'Story') return; // skip story frames
        if (includeDraftOnly && f.approval_status !== 'Draft') return;
        out.push({
          frame_id: f.id,
          day_label: d.day_label,
          date_label: d.date_label,
          theme: d.theme,
          post_type: f.post_type || 'Feed Post',
          current_time: f.post_time || '',
          approval_status: f.approval_status,
          caption_snippet: (f.caption || '').slice(0, 140),
          selected: true,
        });
      });
    });
    return out;
  }, [days, includeDraftOnly]);

  useEffect(() => {
    if (!open) {
      // Reset on close
      setPhase('idle');
      setRows([]);
      setError(null);
      setModelUsed('');
    }
  }, [open]);

  // Auto-trigger AI call when modal opens
  useEffect(() => {
    if (open && phase === 'idle') {
      runAnalysis();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const runAnalysis = async () => {
    if (candidates.length === 0) {
      setRows([]);
      setPhase('review');
      return;
    }
    setPhase('loading');
    setError(null);
    try {
      const { data, error: invokeErr } = await supabase.functions.invoke('auto-schedule', {
        body: {
          posts: candidates.map((c) => ({
            id: c.frame_id,
            day_label: c.day_label,
            date_label: c.date_label,
            theme: c.theme,
            post_type: c.post_type,
            current_time: c.current_time,
            caption_snippet: c.caption_snippet,
          })),
        },
      });
      if (invokeErr) throw new Error(invokeErr.message);
      const suggestions: Suggestion[] = data?.suggestions || [];
      const byId = new Map(suggestions.map((s) => [s.id, s] as const));
      setModelUsed(data?.model_used || '');
      const merged = candidates.map((c) => {
        const s = byId.get(c.frame_id);
        return {
          ...c,
          suggested_time: s?.suggested_time,
          reason: s?.reason,
          selected:
            !!s?.suggested_time &&
            (s?.suggested_time || '').toLowerCase() !== (c.current_time || '').toLowerCase(),
        };
      });
      setRows(merged);
      setPhase('review');
    } catch (err) {
      setError((err as Error).message || 'Failed to analyze posts');
      setPhase('error');
    }
  };

  const toggleRow = (idx: number) => {
    setRows((prev) => prev.map((r, i) => (i === idx ? { ...r, selected: !r.selected } : r)));
  };

  const selectAll = (val: boolean) => {
    setRows((prev) => prev.map((r) => (r.suggested_time ? { ...r, selected: val } : r)));
  };

  const selectedCount = rows.filter((r) => r.selected && r.suggested_time && !r.applied).length;
  const changedCount = rows.filter(
    (r) =>
      r.suggested_time && (r.suggested_time || '').toLowerCase() !== (r.current_time || '').toLowerCase(),
  ).length;

  const applyAll = async () => {
    const toApply = rows.filter((r) => r.selected && r.suggested_time && !r.applied);
    if (toApply.length === 0) {
      toast.info('Nothing selected', { description: 'Tick at least one row to apply.' });
      return;
    }
    setPhase('applying');
    let success = 0;
    let failed = 0;
    for (const r of toApply) {
      try {
        await updateFrame(r.frame_id, { post_time: r.suggested_time! });
        setRows((prev) =>
          prev.map((row) =>
            row.frame_id === r.frame_id
              ? { ...row, applied: true, current_time: r.suggested_time! }
              : row,
          ),
        );
        success++;
      } catch {
        failed++;
      }
    }
    setPhase('done');
    if (failed === 0) {
      toast.success(`Applied ${success} suggested time${success === 1 ? '' : 's'}`, {
        description: 'Post times updated across the planner.',
      });
    } else {
      toast.warning(`Applied ${success}, ${failed} failed`, {
        description: 'Some updates did not complete. Check the planner and retry.',
      });
    }
    onUpdated?.();
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto">
      <div className="bg-white w-full max-w-6xl my-6 shadow-2xl border border-slate-200 rounded-sm">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-slate-100 bg-gradient-to-r from-[#0D1B2A] to-[#1A3B5C] text-white rounded-t-sm">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded bg-[#C9A961]/15 border border-[#C9A961]/40 flex items-center justify-center">
              <Clock className="w-4 h-4 text-[#C9A961]" />
            </div>
            <div>
              <h2 className="font-serif text-lg font-light">
                Auto-Schedule
                <span className="ml-3 text-[#C9A961] text-[10px] tracking-[0.25em] uppercase">
                  AI · Optimal Post Times
                </span>
              </h2>
              <p className="font-sans text-[11px] text-white/60 mt-0.5">
                Suggested post times based on post type, theme, and engagement research.
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-white/10 transition-colors rounded"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Toolbar */}
        <div className="flex items-center justify-between gap-3 px-5 py-3 border-b border-slate-100 bg-slate-50">
          <div className="flex items-center gap-4">
            <label className="inline-flex items-center gap-2 font-sans text-[11px] tracking-wider uppercase text-slate-600 cursor-pointer">
              <input
                type="checkbox"
                checked={includeDraftOnly}
                onChange={(e) => setIncludeDraftOnly(e.target.checked)}
                className="accent-[#C9A961]"
              />
              Draft posts only
            </label>
            <span className="font-sans text-[11px] text-slate-500">
              {candidates.length} eligible · {changedCount} suggested change
              {changedCount === 1 ? '' : 's'}
              {modelUsed && (
                <span className="ml-2 text-[#8B6F47]">
                  · model: <span className="font-mono">{modelUsed}</span>
                </span>
              )}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => selectAll(true)}
              disabled={phase !== 'review' && phase !== 'done'}
              className="font-sans text-[10px] tracking-wider uppercase px-3 py-1.5 border border-slate-200 text-slate-600 hover:border-[#C9A961] hover:text-[#C9A961] disabled:opacity-40 transition-colors"
            >
              Select All
            </button>
            <button
              onClick={() => selectAll(false)}
              disabled={phase !== 'review' && phase !== 'done'}
              className="font-sans text-[10px] tracking-wider uppercase px-3 py-1.5 border border-slate-200 text-slate-600 hover:border-slate-400 disabled:opacity-40 transition-colors"
            >
              Clear
            </button>
            <button
              onClick={runAnalysis}
              disabled={phase === 'loading' || phase === 'applying'}
              className="inline-flex items-center gap-1.5 font-sans text-[10px] tracking-wider uppercase px-3 py-1.5 border border-[#C9A961]/40 bg-[#C9A961]/5 text-[#8B6F47] hover:bg-[#C9A961] hover:text-white disabled:opacity-40 transition-colors"
            >
              <RefreshCw className={`w-3 h-3 ${phase === 'loading' ? 'animate-spin' : ''}`} />
              {phase === 'loading' ? 'Analyzing…' : 'Re-Analyze'}
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="max-h-[60vh] overflow-y-auto">
          {phase === 'loading' && (
            <div className="py-20 flex flex-col items-center justify-center gap-4">
              <Loader2 className="w-10 h-10 text-[#C9A961] animate-spin" />
              <div className="text-center">
                <p className="font-serif text-base text-[#1A3B5C]">
                  Analyzing {candidates.length} post{candidates.length === 1 ? '' : 's'}…
                </p>
                <p className="font-sans text-xs text-slate-500 mt-1">
                  Pulling optimal post times based on type, theme, and engagement research.
                </p>
              </div>
            </div>
          )}

          {phase === 'error' && (
            <div className="py-16 flex flex-col items-center gap-3">
              <AlertCircle className="w-8 h-8 text-red-500" />
              <p className="font-sans text-sm text-red-600">{error}</p>
              <button
                onClick={runAnalysis}
                className="mt-2 inline-flex items-center gap-2 px-4 py-2 border border-slate-200 font-sans text-xs uppercase tracking-wider text-slate-600 hover:border-[#C9A961] hover:text-[#C9A961]"
              >
                <RefreshCw className="w-3.5 h-3.5" /> Try Again
              </button>
            </div>
          )}

          {(phase === 'review' || phase === 'applying' || phase === 'done') && rows.length === 0 && (
            <div className="py-16 text-center">
              <p className="font-serif text-base text-[#1A3B5C] mb-1">No eligible posts</p>
              <p className="font-sans text-xs text-slate-500">
                {includeDraftOnly
                  ? 'Uncheck "Draft posts only" to include Approved & Scheduled posts.'
                  : 'There are no feed posts to analyze yet.'}
              </p>
            </div>
          )}

          {(phase === 'review' || phase === 'applying' || phase === 'done') && rows.length > 0 && (
            <table className="w-full text-left">
              <thead className="bg-slate-50 border-b border-slate-200 sticky top-0">
                <tr>
                  <th className="px-3 py-3 w-10"></th>
                  <th className="px-3 py-3 font-sans text-[9px] tracking-[0.2em] uppercase text-slate-500">
                    Day
                  </th>
                  <th className="px-3 py-3 font-sans text-[9px] tracking-[0.2em] uppercase text-slate-500">
                    Type
                  </th>
                  <th className="px-3 py-3 font-sans text-[9px] tracking-[0.2em] uppercase text-slate-500">
                    Theme
                  </th>
                  <th className="px-3 py-3 font-sans text-[9px] tracking-[0.2em] uppercase text-slate-500 text-center">
                    Current
                  </th>
                  <th className="px-3 py-3 w-6"></th>
                  <th className="px-3 py-3 font-sans text-[9px] tracking-[0.2em] uppercase text-[#C9A961] text-center">
                    Suggested
                  </th>
                  <th className="px-3 py-3 font-sans text-[9px] tracking-[0.2em] uppercase text-slate-500">
                    Reason
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, idx) => {
                  const changed =
                    !!r.suggested_time &&
                    (r.suggested_time || '').toLowerCase() !== (r.current_time || '').toLowerCase();
                  return (
                    <tr
                      key={r.frame_id}
                      className={`border-b border-slate-100 ${
                        r.applied ? 'bg-emerald-50/40' : changed ? 'hover:bg-slate-50' : ''
                      }`}
                    >
                      <td className="px-3 py-3 align-top">
                        {r.applied ? (
                          <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                        ) : r.suggested_time ? (
                          <input
                            type="checkbox"
                            checked={r.selected}
                            onChange={() => toggleRow(idx)}
                            disabled={!changed}
                            className="accent-[#C9A961] disabled:opacity-30"
                            title={changed ? 'Include in apply' : 'No change suggested'}
                          />
                        ) : (
                          <span className="text-slate-300">—</span>
                        )}
                      </td>
                      <td className="px-3 py-3 align-top">
                        <div className="font-serif text-sm text-[#1A3B5C]">{r.day_label}</div>
                        <div className="font-sans text-[10px] tracking-wider uppercase text-[#8B6F47]">
                          {r.date_label}
                        </div>
                      </td>
                      <td className="px-3 py-3 align-top">
                        <span
                          className={`inline-block px-2 py-0.5 font-sans text-[9px] tracking-wider uppercase border ${
                            r.post_type === 'Reel'
                              ? 'border-purple-300 bg-purple-50 text-purple-700'
                              : r.post_type === 'Carousel'
                                ? 'border-blue-300 bg-blue-50 text-blue-700'
                                : 'border-slate-300 bg-slate-50 text-slate-700'
                          }`}
                        >
                          {r.post_type}
                        </span>
                      </td>
                      <td className="px-3 py-3 align-top">
                        <div className="font-sans text-xs text-slate-700 max-w-[180px] truncate">
                          {r.theme || <span className="italic text-slate-400">No theme</span>}
                        </div>
                      </td>
                      <td className="px-3 py-3 align-top text-center">
                        <span className="font-mono text-xs text-slate-600">
                          {r.current_time || <span className="italic text-slate-400">—</span>}
                        </span>
                      </td>
                      <td className="px-3 py-3 align-top text-center">
                        {changed ? (
                          <ArrowRight className="w-4 h-4 text-[#C9A961] mx-auto" />
                        ) : (
                          <span className="text-slate-300">·</span>
                        )}
                      </td>
                      <td className="px-3 py-3 align-top text-center">
                        {r.suggested_time ? (
                          <span
                            className={`inline-block font-mono text-xs font-semibold px-2 py-0.5 rounded ${
                              changed
                                ? 'bg-[#C9A961]/15 text-[#8B6F47] border border-[#C9A961]/40'
                                : 'bg-slate-100 text-slate-500 border border-slate-200'
                            }`}
                          >
                            {r.suggested_time}
                          </span>
                        ) : (
                          <span className="text-slate-300">—</span>
                        )}
                      </td>
                      <td className="px-3 py-3 align-top">
                        <p className="font-sans text-[11px] text-slate-600 leading-snug max-w-[320px]">
                          {r.reason || (
                            <span className="italic text-slate-400">No reasoning returned</span>
                          )}
                        </p>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between gap-3 p-5 border-t border-slate-100 bg-slate-50 rounded-b-sm">
          <div className="font-sans text-[11px] text-slate-600">
            {phase === 'review' && rows.length > 0 && (
              <>
                <span className="font-semibold text-[#1A3B5C]">{selectedCount}</span> of {rows.length}{' '}
                selected ·{' '}
                <span className="text-[#8B6F47]">{changedCount} suggested change{changedCount === 1 ? '' : 's'}</span>
              </>
            )}
            {phase === 'done' && (
              <span className="inline-flex items-center gap-1.5 text-emerald-700 font-semibold">
                <Check className="w-3.5 h-3.5" /> Updates applied. Close to refresh the planner.
              </span>
            )}
            {phase === 'applying' && (
              <span className="inline-flex items-center gap-1.5 text-[#8B6F47]">
                <Loader2 className="w-3.5 h-3.5 animate-spin" /> Applying updates…
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={onClose}
              className="px-4 py-2 border border-slate-200 font-sans text-[11px] tracking-[0.2em] uppercase text-slate-600 hover:border-slate-400 transition-colors"
            >
              {phase === 'done' ? 'Close' : 'Cancel'}
            </button>
            <button
              onClick={applyAll}
              disabled={phase !== 'review' || selectedCount === 0}
              className="inline-flex items-center gap-2 px-5 py-2 bg-gradient-to-r from-[#1A3B5C] to-[#0D1B2A] text-white font-sans text-[11px] tracking-[0.2em] uppercase hover:from-[#C9A961] hover:to-[#8B6F47] disabled:opacity-40 disabled:cursor-not-allowed transition-all"
            >
              <Sparkles className="w-3.5 h-3.5 text-[#C9A961]" />
              Apply All Suggested Times
              {selectedCount > 0 && (
                <span className="ml-1 px-1.5 py-0.5 bg-white/15 rounded text-[10px]">
                  {selectedCount}
                </span>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AutoScheduleModal;
